import React, { useState, useEffect } from "react";
import CustomerPage from "./CustomerPage";
import { Helmet } from "react-helmet";
import { FiMapPin, FiInfo, FiPhone } from "react-icons/fi";
import { FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchTripStatus } from "../../Services/ApiCallsServices/HomeDashboardApi";
import { PulseLoader } from 'react-spinners';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";

const CustomerTripProgress = () => {
  const [tripDetails, setTripDetails] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const tripReference = useSelector((state) => state.notification.selectedTripRef);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tripReference) {
      setError("Trip reference is missing");
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const loadTripDetails = async () => {
      try {
        const details = await fetchTripStatus(tripReference);
        if (!isActive) return;
        setTripDetails(details);
        setStatus(details.status);
        setError(null);
      } catch (err) {
        if (!isActive) return;
        setError("Error fetching trip details.");
        toast.error("Error fetching trip details. Please try again later.");
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadTripDetails();

    const iv = setInterval(loadTripDetails, 10000);
    return () => {
      isActive = false;
      clearInterval(iv);
    };
  }, [tripReference]);

  const getStatusColor = () => {
    switch(status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
        <CustomerPage>
          <Helmet>
            <title>Loading Trip Status | Storage Sprint</title>
          </Helmet>
          <div className="flex justify-center items-center h-64">
            <PulseLoader color="#FFD700" size={15} />
          </div>
        </CustomerPage>
    );
  }

  if (error) {
    return (
        <CustomerPage>
          <Helmet>
            <title>Trip Status Error | Storage Sprint</title>
          </Helmet>
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Trip</h3>
            <p className="text-gray-600">{error}</p>
          </div>
        </CustomerPage>
    );
  }

  if (!tripDetails) {
    return (
        <CustomerPage>
          <Helmet>
            <title>No Trip Found | Storage Sprint</title>
          </Helmet>
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 text-center">
            <div className="text-yellow-500 text-4xl mb-4">🚛</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Trip Details Found</h3>
            <p className="text-gray-600">Please check back later or contact support</p>
          </div>
        </CustomerPage>
    );
  }

  return (
      <CustomerPage>
        <Helmet>
          <title>Trip Status | Storage Sprint</title>
        </Helmet>

        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                <span className="text-yellow-500">Your Trip</span> Status
              </h1>
              <p className="text-gray-600">Real-time updates on your delivery progress</p>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {/* Status Banner */}
              <div className={`px-6 py-4 ${getStatusColor()} flex items-center`}>
                <FiInfo className="mr-2" />
                <span className="font-medium">Current Status:</span>
                <span className="ml-2 font-semibold capitalize">{status}</span>
              </div>

              {/* Trip Details */}
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <FaUser className="text-yellow-500 mr-2" />
                  Driver Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FaUser className="text-gray-500 mr-2" />
                      <span className="font-medium text-gray-700">Driver Name</span>
                    </div>
                    <p className="text-gray-900">{tripDetails.driverName || 'Not assigned yet'}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FiPhone className="text-gray-500 mr-2" />
                      <span className="font-medium text-gray-700">Contact Number</span>
                    </div>
                    <p className="text-gray-900">{tripDetails.driverNumber || 'Not available'}</p>
                  </div>
                </div>

                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <FiMapPin className="text-yellow-500 mr-2" />
                  Trip Locations
                </h2>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FiMapPin className="text-green-500 mr-2" />
                      <span className="font-medium text-gray-700">Pickup Location</span>
                    </div>
                    <p className="text-gray-900">{tripDetails.pickupLocation}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <FiMapPin className="text-red-500 mr-2" />
                      <span className="font-medium text-gray-700">Dropoff Location</span>
                    </div>
                    <p className="text-gray-900">{tripDetails.dropoffLocation}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Feedback invitation section */}
            {status === "Completed" && (
                <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-xl shadow-md text-center">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Thank you for completing your trip!
                  </h3>
                  <p className="text-gray-600 mb-4">
                    We'd love to hear about your experience. Your feedback helps us improve.
                  </p>
                  <button
                      onClick={() => navigate("/feedback")}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg transition"
                  >
                    Leave Feedback
                  </button>
                </div>
            )}
          </div>
        </div>
      </CustomerPage>
  );
};

export default CustomerTripProgress;
